<script setup>
import { computed, ref } from 'vue'
import { Head } from '@inertiajs/vue3'
import { FileText, Search, FolderOpen, MessageCircle, X } from 'lucide-vue-next'
import PageHeader from '../Components/PageHeader.vue'
import Card from '../Components/Card.vue'
import CtaButton from '../Components/CtaButton.vue'
import DocumentList from '../Components/DocumentList.vue'

const props = defineProps({
  aircraft: { type: Object, required: true },
  categories: { type: Array, required: true },
  groups: { type: Array, required: true },
})

const term = ref('')
const category = ref(null)

const allDocuments = computed(() => props.groups.flatMap((g) => g.documents))

// Filtro só no cliente: o volume de documentos por OS é pequeno.
const matches = (doc) => {
  const byCategory = !category.value || doc.category === category.value
  const byTerm = doc.name.toLowerCase().includes(term.value.trim().toLowerCase())
  return byCategory && byTerm
}

const filteredGroups = computed(() =>
  props.groups
    .map((group) => ({ ...group, documents: group.documents.filter(matches) }))
    .filter((group) => group.documents.length > 0),
)

const resultCount = computed(() =>
  filteredGroups.value.reduce((sum, g) => sum + g.documents.length, 0),
)

const countFor = (name) => allDocuments.value.filter((d) => d.category === name).length

const filtering = computed(() => Boolean(term.value.trim() || category.value))

const clear = () => {
  term.value = ''
  category.value = null
}
</script>

<template>
  <Head title="Documentos" />

  <div class="space-y-6">
    <PageHeader title="DOCUMENTOS" :aircraft="aircraft" />

    <div class="grid grid-cols-1 gap-6 xl:grid-cols-3">
      <div class="space-y-6 xl:col-span-2">
        <Card title="Arquivos" :icon="FolderOpen">
          <!-- Busca -->
          <div class="relative">
            <Search class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <label for="busca-documentos" class="sr-only">Buscar documento pelo nome</label>
            <input
              id="busca-documentos"
              v-model="term"
              type="search"
              placeholder="Buscar por nome do documento"
              class="w-full rounded-lg border border-slate-200 bg-white py-2.5 pl-9 pr-3 text-sm text-up-navy placeholder:text-slate-400 focus:border-up-orange focus:outline-none"
            />
          </div>

          <!-- Filtro por categoria -->
          <div class="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              class="rounded-full px-3 py-1.5 text-xs font-medium transition-colors"
              :class="category === null ? 'bg-up-navy text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'"
              :aria-pressed="category === null"
              @click="category = null"
            >
              Todos ({{ allDocuments.length }})
            </button>
            <button
              v-for="name in categories"
              :key="name"
              type="button"
              class="rounded-full px-3 py-1.5 text-xs font-medium transition-colors"
              :class="category === name ? 'bg-up-navy text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'"
              :aria-pressed="category === name"
              @click="category = name"
            >
              {{ name }} ({{ countFor(name) }})
            </button>
          </div>

          <p v-if="filtering" class="mt-3 flex items-center gap-2 text-xs text-slate-500" role="status">
            {{ resultCount }} {{ resultCount === 1 ? 'documento encontrado' : 'documentos encontrados' }}
            <button
              type="button"
              class="inline-flex items-center gap-1 font-medium text-up-orange hover:underline"
              @click="clear"
            >
              <X class="h-3 w-3" aria-hidden="true" />limpar filtros
            </button>
          </p>

          <!-- Resultados -->
          <div v-if="filteredGroups.length" class="mt-5 space-y-6">
            <section v-for="group in filteredGroups" :key="group.label">
              <h3 class="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
                {{ group.label }}
              </h3>
              <p class="mb-2 text-xs text-slate-500">{{ group.hint }}</p>
              <DocumentList :documents="group.documents" />
            </section>
          </div>

          <p v-else class="mt-8 mb-4 text-center text-sm text-slate-500">
            Nenhum documento corresponde à busca.
          </p>
        </Card>
      </div>

      <div class="space-y-6">
        <Card title="Resumo" :icon="FileText">
          <dl class="divide-y divide-slate-100">
            <div v-for="name in categories" :key="name" class="flex items-center justify-between py-3">
              <dt class="text-sm text-slate-500">{{ name }}</dt>
              <dd class="text-sm font-semibold text-up-navy">{{ countFor(name) }}</dd>
            </div>
            <div class="flex items-center justify-between py-3">
              <dt class="text-sm font-medium text-up-navy">Total</dt>
              <dd class="text-sm font-bold text-up-navy">{{ allDocuments.length }}</dd>
            </div>
          </dl>
        </Card>

        <Card>
          <p class="text-sm leading-relaxed text-slate-600">
            Precisa de um documento que não está aqui? A equipe técnica providencia e disponibiliza
            nesta área.
          </p>
          <div class="pt-4">
            <CtaButton href="/suporte?assunto=documento" variant="outline" :icon="MessageCircle">
              Solicitar documento
            </CtaButton>
          </div>
        </Card>
      </div>
    </div>
  </div>
</template>
